import type Pageable from 'models/Pageable';
import { formattingToFactoringNoticeDetailVOModel } from 'models/vo/FactoringNoticeDetailVO';
import type { FactoringNoticeDetailVO, FactoringNoticeDetailVOModel } from 'models/vo/FactoringNoticeDetailVO';
import { formattingToFactoringNoticeReceiverVOModel } from 'models/vo/FactoringNoticeReceiverVO';
import type { FactoringNoticeReceiverVO, FactoringNoticeReceiverVOModel } from 'models/vo/FactoringNoticeReceiverVO';
import { formattingToFactoringNoticeRelatedSuccessArVOModel } from 'models/vo/FactoringNoticeRelatedSuccessArVO';
import type {
  FactoringNoticeRelatedSuccessArVO,
  FactoringNoticeRelatedSuccessArVOModel,
} from 'models/vo/FactoringNoticeRelatedSuccessArVO';
import { formattingToFactoringNoticeVOModel } from 'models/vo/FactoringNoticeVO';
import type { FactoringNoticeVO, FactoringNoticeVOModel } from 'models/vo/FactoringNoticeVO';
import http from 'utils/http';
import API_FI from 'utils/http/api/financier';

import type { FinancierFactoringNoticeReceiversListRequest } from './request';
import type { ATTACHMENT_TYPE } from '../../../../../enums';

export async function requestFinancierFactoringNoticeList(
  pageNumber: number,
  rowCount: number,
): Promise<Pageable<FactoringNoticeVOModel[]>> {
  const response = await http.get<Pageable<FactoringNoticeVO[]>>({
    url: API_FI.FACTORING_NOTICES.FACTORING_NOTICE,
    data: {
      pageNumber,
      rowCount,
    },
  });
  const factoringNoticeList: FactoringNoticeVOModel[] = response.content.map((data: FactoringNoticeVO) =>
    formattingToFactoringNoticeVOModel(data),
  );

  return { ...response, content: factoringNoticeList };
}

export async function requestFinancierFactoringNoticeDetail(
  factoringNoticeId: number,
): Promise<FactoringNoticeDetailVOModel> {
  const response = await http.get<FactoringNoticeDetailVO>({
    url: API_FI.FACTORING_NOTICES.FACTORING_NOTICE_DETAIL(factoringNoticeId),
  });

  return formattingToFactoringNoticeDetailVOModel(response);
}

export async function requestFinancierFactoringNoticeDownload(
  factoringNoticeId: number,
  data: { attachmentType: ATTACHMENT_TYPE },
) {
  await http.download(API_FI.FACTORING_NOTICES.FACTORING_NOTICE_DOWNLOAD_ATTACHMENT(factoringNoticeId), data);
}

export async function requestFinancierFactoringNoticeResend(
  factoringNoticeId: number,
  factoringNoticeReceiverId?: number, // Null 일 경우 성공건 제외 전체 재발송
) {
  await http.post<void>({
    url: API_FI.FACTORING_NOTICES.FACTORING_NOTICE_RESEND(factoringNoticeId),
    data: {
      factoringNoticeReceiverId,
    },
    hasLoadingUI: false,
  });
}

export async function requestFinancierFactoringNoticeReceiverList(
  factoringNoticeId: number,
  pageNumber: number,
  rowCount: number,
  searchValue?: FinancierFactoringNoticeReceiversListRequest,
): Promise<Pageable<FactoringNoticeReceiverVOModel[]>> {
  const response = await http.get<Pageable<FactoringNoticeReceiverVO[]>>({
    url: API_FI.FACTORING_NOTICES.FACTORING_NOTICE_RECEIVERS(factoringNoticeId),
    data: {
      pageNumber,
      rowCount,
      ...searchValue,
    },
  });
  const factoringNoticeReceiverList: FactoringNoticeReceiverVOModel[] = response.content.map(
    (data: FactoringNoticeReceiverVO) => formattingToFactoringNoticeReceiverVOModel(data),
  );

  return { ...response, content: factoringNoticeReceiverList };
}

export async function requestFinancierFactoringNoticeRelatedArsList(
  factoringNoticeId: number,
  pageNumber: number,
  rowCount: number,
): Promise<Pageable<FactoringNoticeRelatedSuccessArVOModel[]>> {
  const response = await http.get<Pageable<FactoringNoticeRelatedSuccessArVO[]>>({
    url: API_FI.FACTORING_NOTICES.FACTORING_NOTICE_RELATED_ARS(factoringNoticeId),
    data: {
      pageNumber,
      rowCount,
    },
  });
  const factoringNoticeRelatedArsList: FactoringNoticeRelatedSuccessArVOModel[] = response.content.map(
    (data: FactoringNoticeRelatedSuccessArVO) => formattingToFactoringNoticeRelatedSuccessArVOModel(data),
  );

  return { ...response, content: factoringNoticeRelatedArsList };
}
